import { useEffect } from 'react';

export type ColourMode = 'light' | 'dark' | 'auto';
export type ResolvedColourMode = 'light' | 'dark';

const PREFERS_DARK_QUERY = '(prefers-color-scheme: dark)';

/**
 * Clamps an arbitrary value (e.g. a user setting or `localStorage` value, neither of which is
 * validated) to a valid {@link ColourMode}, defaulting to `auto`.
 */
export const toColourMode = (value: unknown): ColourMode => {
  return value === 'light' || value === 'dark' ? value : 'auto';
};

export const resolveColourMode = (mode: ColourMode, prefersDark: boolean): ResolvedColourMode => {
  if (mode === 'auto') {
    return prefersDark ? 'dark' : 'light';
  }

  return mode;
};

/**
 * Applies a colour mode to the whole document, via Bootstrap's `data-bs-theme` attribute on
 * `<html>`. For `auto`, follows the device's colour scheme, including live changes.
 */
export const useApplyColourMode = (mode: ColourMode) => {
  useEffect(() => {
    const root = document.documentElement;

    if (mode !== 'auto') {
      root.setAttribute('data-bs-theme', mode);
      return;
    }

    const query = window.matchMedia(PREFERS_DARK_QUERY);
    const apply = () => root.setAttribute('data-bs-theme', resolveColourMode(mode, query.matches));

    apply();
    query.addEventListener('change', apply);

    return () => {
      query.removeEventListener('change', apply);
    };
  }, [mode]);
};
